import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import { isEmpty } from 'lodash';
import get from 'lodash/get';
import React, { useState } from 'react';
import {
  UpdateOrderRequest,
  isPresent,
  AddOnOption,
  TRANSACTION_ACCOUNT_PATH_2,
  INVOICE_GROUP_PATH_2,
} from '@atlassian/cssxp-typescommon';
import { AddOn, InvoiceGroup, Maybe } from '@css-xp/graphtypes';
import StatuspageAvailableAddOnsFormFieldV2 from './statuspage-available-add-ons-form-field-v2';
import { updateEntitlementPlan } from '@atlassian/temp-proxies';
import { InvoiceGroupFormFieldV2, InvoiceGroupSelectItemV2 } from './invoice-group-form-field-v2';
import { useDispatch } from 'react-redux';
import { ApolloQueryResult } from '@apollo/client';
import { useFormSubmit, useNotification } from '../../hooks';
import { orderSuccessNotification } from '../../notifications';
import { InfoPanel } from '../../info-panel';
import { ErrorMessageOnInfoPanel } from '../../error-page';
import { actions as SidebarActions } from '../../state';
import { TransactionAccountContextType } from '../../common-context';
import EntitlementIcon from '../../entitlement-icon/entitlement-icon';
import {
  NoBillingProfileWarningPanel,
  EmptyBillingProfileWarningPanel,
  EMPTY_BILLING_PROFILE_WARNING_MESSAGES,
} from '../../entitlement-utils';
import { logTimeStamp } from '@atlassian/temp-metal';
import { SignalFxFEPerfStore } from '@atlassian/temp-helper/constants';
import { EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';

interface Props {
  entitlement: EntitlementDetails;
  availableAddOns: AddOn[] | null;
  hasPaymentMethod: boolean;
  transactionAccountContext: TransactionAccountContextType;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
}

const removeEmptyAddOns = (addOn: AddOn): addOn is AddOn => !isEmpty(addOn);

const formatAddonsForFormFields = (addOn: AddOn) => ({
  name: addOn.name,
  offeringId: addOn.offeringId,
  units: 1,
});

const removeAddOnsWithEmptyFields = (addOn: AddOn): addOn is AddOnOption =>
  !isEmpty(addOn.name) && !isEmpty(addOn.offeringId);

export const SubscribeToAddOnsFormV2 = ({
  entitlement,
  availableAddOns,
  refetch,
  transactionAccountContext,
}: Props) => {
  const startTime = new Date().getTime();
  const dispatch = useDispatch();

  const [selectedAddOns, setSelectedAddOns] = useState<AddOnOption[]>([{}]);
  const [selectedInvoiceGroup, setSelectedInvoiceGroup] = useState<InvoiceGroupSelectItemV2 | undefined>();

  const { error, isProcessing, submitForm, complete, data } = useFormSubmit<UpdateOrderRequest, any>({
    submitFormAction: updateEntitlementPlan,
    onComplete: () => {
      dispatch(SidebarActions.closeSidebar());
      refetch();
    },
  });

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  const { getTransactionAccount, data: transactionAccountData } = transactionAccountContext;

  const availableAddOnsFormOptions: AddOnOption[] =
    availableAddOns?.filter(removeEmptyAddOns).map(formatAddonsForFormFields).filter(removeAddOnsWithEmptyFields) ?? [];

  const removeAddOn = (indexToRemove: number) => {
    setSelectedAddOns((addOns) => addOns.filter((_addOn, index) => indexToRemove !== index));
  };

  const addAdditionalAddOn = () => {
    setSelectedAddOns((addOns) => [...addOns, {}]);
  };

  const updateSelectedAddOn = (selectedAddOn: AddOnOption, selectedIndex: number) => {
    setSelectedAddOns((addOns) => {
      const isExisitingAddOn = addOns.findIndex((item) => item.name === selectedAddOn.name);

      if (isExisitingAddOn !== -1) {
        return addOns;
      }

      const updatedAddOns = [...addOns];
      updatedAddOns[selectedIndex] = selectedAddOn;
      return updatedAddOns;
    });
  };

  const updateSelectedAddOnQuantity = (units: number, selectedIndex: number) => {
    setSelectedAddOns((addOns) => {
      const updatedAddOns = [...addOns];
      updatedAddOns[selectedIndex].units = units;
      return updatedAddOns;
    });
  };

  const handleSubmit = () => {
    logTimeStamp(startTime, 'Subscribe to add-ons', SignalFxFEPerfStore.FE_USER_TASK_SUB_TO_ADDON_TIME);

    const nextBillingAnchorTimestamp = entitlement?.latestPeriodEndDate
      ? new Date(entitlement?.latestPeriodEndDate).getTime().toString()
      : '';

    const formattedAddOnSubscriptionRequests = selectedAddOns
      ?.map((addOn) => ({
        offeringId: addOn.offeringId,
        units: addOn.units?.toString(),
        nextBillingAnchorTimestamp,
      }))
      .filter(isPresent);

    const requestData: UpdateOrderRequest = {
      invoiceGroupId: get(selectedInvoiceGroup, 'value', ''),
      entitlementId: entitlement.id,
      //@ts-ignore
      addOns: formattedAddOnSubscriptionRequests,
      productId: entitlement?.productId ?? '',
      nextBillingAnchorTimestamp,
    };

    submitForm(requestData);
  };

  const updateSelectedInvoiceGroup = (selectedInvoiceGroup: InvoiceGroupSelectItemV2) => {
    setSelectedInvoiceGroup(selectedInvoiceGroup);
  };

  if (complete && !notificationSent) {
    const transitionTimeError =
      data.transitionTime === 'unknown'
        ? ' We were unable to determine if the successfully submitted order is immediate or scheduled.'
        : undefined;
    const notificationOptions = orderSuccessNotification(
      'SUBSCRIBE_TO_ADDONS_V2',
      data.transitionTime,
      transitionTimeError,
      true
    );
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  const transactionAccount = getTransactionAccount(transactionAccountData);
  // Check if there is a payment method in the transaction account
  const hasPaymentMethod = transactionAccount?.availablePaymentMethods?.totalCount !== 0;

  const availableInvoiceGroups = (transactionAccount?.availableInvoiceGroups?.nodes ?? []) as Maybe<InvoiceGroup[]>;

  const defaultBillingGroupId = entitlement?.activeSubscriptionBillingGroupId ?? entitlement?.billingGroupId;

  const noSoldTOAddressForSelectedInvoiceGroup =
    selectedInvoiceGroup && !selectedInvoiceGroup?.invoiceGroup?.shipToParty;

  const noPaymentMethodForInvoiceGroup =
    selectedInvoiceGroup && !selectedInvoiceGroup?.invoiceGroup?.defaultPaymentMethod;

  let warningBanner: React.ReactNode;

  if (availableInvoiceGroups && availableInvoiceGroups.length < 1) {
    warningBanner = (
      <NoBillingProfileWarningPanel
        routeLink={`${TRANSACTION_ACCOUNT_PATH_2}/${transactionAccount?.transactionAccountNumber}`}
      />
    );
  } else if (noSoldTOAddressForSelectedInvoiceGroup && noPaymentMethodForInvoiceGroup) {
    warningBanner = (
      <EmptyBillingProfileWarningPanel
        routeLink={`${INVOICE_GROUP_PATH_2}/${selectedInvoiceGroup?.value}`}
        errors={[
          EMPTY_BILLING_PROFILE_WARNING_MESSAGES.noPaymentMethod,
          EMPTY_BILLING_PROFILE_WARNING_MESSAGES.noSoldToAddress,
        ]}
      />
    );
  } else if (noSoldTOAddressForSelectedInvoiceGroup) {
    warningBanner = (
      <EmptyBillingProfileWarningPanel
        routeLink={`${INVOICE_GROUP_PATH_2}/${selectedInvoiceGroup?.value}`}
        errors={[EMPTY_BILLING_PROFILE_WARNING_MESSAGES.noSoldToAddress]}
      />
    );
  } else if (noPaymentMethodForInvoiceGroup) {
    warningBanner = (
      <EmptyBillingProfileWarningPanel
        routeLink={`${INVOICE_GROUP_PATH_2}/${selectedInvoiceGroup?.value}`}
        errors={[EMPTY_BILLING_PROFILE_WARNING_MESSAGES.noPaymentMethod]}
      />
    );
  }

  const isFormInvalid =
    !selectedInvoiceGroup ||
    !!selectedAddOns.find((item) => !item.name) ||
    noPaymentMethodForInvoiceGroup ||
    noSoldTOAddressForSelectedInvoiceGroup ||
    !hasPaymentMethod;

  return (
    <Form onSubmit={handleSubmit}>
      {({ formProps }) => (
        <form {...formProps}>
          <FormHeader title="Subscribe to additional allocations">
            {!!entitlement?.productId && (
              <EntitlementIcon productId={entitlement?.productId} isCloud={true} className="avatar medium" />
            )}
            <h4 style={{ display: 'inline' }}>{`${entitlement?.productName ?? 'Unnamed product'} ${
              entitlement?.entitlementNumber
            }`}</h4>
          </FormHeader>
          <FormSection>
            <div className="subscribe-to-add-ons-info-banner">
              {!hasPaymentMethod && (
                <InfoPanel
                  type="warning"
                  title="Cannot subscribe to add-ons - there are no payment methods on file for this entitlement"
                  message={''}
                />
              )}
              {warningBanner}
              {error && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorMessageOnInfoPanel
                      title="We're unable to subscribe to add ons for the following reason"
                      message={error}
                    />
                  }
                />
              )}
            </div>
            <StatuspageAvailableAddOnsFormFieldV2
              availableAddOns={availableAddOnsFormOptions}
              selectedAddOns={selectedAddOns}
              removeAddOn={removeAddOn}
              addAdditionalAddOn={addAdditionalAddOn}
              updateSelectedAddOnQuantity={updateSelectedAddOnQuantity}
              updateSelectedAddOn={updateSelectedAddOn}
            />
          </FormSection>
          <FormSection>
            <InvoiceGroupFormFieldV2
              selectedInvoiceGroup={selectedInvoiceGroup}
              productId={entitlement?.productId ?? undefined}
              availableInvoiceGroups={availableInvoiceGroups}
              entitlementBillingInvoiceGroupId={defaultBillingGroupId}
              scheduledBillingInvoiceGroupId={entitlement?.scheduledChanges?.scheduledBillingInvoiceGroupId}
              isRequired
              onChange={updateSelectedInvoiceGroup}
            />
          </FormSection>
          <FormFooter align="end">
            <ButtonGroup>
              <Button onClick={() => dispatch(SidebarActions.closeSidebar())}>Cancel</Button>
              <LoadingButton
                appearance="primary"
                type="submit"
                isLoading={isProcessing}
                isDisabled={isFormInvalid}
                testId="submit-add-on"
              >
                Subscribe
              </LoadingButton>
            </ButtonGroup>
          </FormFooter>
        </form>
      )}
    </Form>
  );
};
